/**
 * ElectronicModuleEditorModal — create/edit form for an `electronicModules`
 * entity. Mirrors CustomCalibreModal's shape (manufacturer picker + co-developer
 * multi-select, lookup-backed pickers, alias chips, image editor), but writes the
 * shared-types `ElectronicModule` directly via `saveElectronicModule`. AI enrich
 * uses the module-specific `AiElectronicModuleEnrichPanel` (per-field results
 * from the `enrichElectronicModule` callable) feeding the apply-review diff.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import {
  saveElectronicModule,
  deleteElectronicModule,
  uploadElectronicModuleImage,
  deleteCalibreImage,
  fetchImageBlob,
  getImageUrl,
  buildElectronicModuleDocId,
  enrichElectronicModuleWithAI,
  type CalibreImage,
} from '../../services/firebase';
import type { ElectronicModule } from '../../types';
import { useManufacturerCatalogue } from '../../hooks/useManufacturerCatalogue';
import { LookupFieldSelect } from '../CuratedWatches/LookupFieldSelect';
import { LookupMultiSelect } from '../CuratedWatches/LookupMultiSelect';
import { TagInput } from '../CuratedWatches/TagInput';
import {
  AiElectronicModuleEnrichPanel,
  type ModuleAiEnrichState,
} from './AiElectronicModuleEnrichPanel';
import { MovementManufacturerQuickCreate } from '../MovementManufacturers';
import { moduleResultToFields } from './aiModuleEnrich';
import toast from 'react-hot-toast';

const MAX_IMAGES = 4;
const inputClass =
  'w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500';

interface ModuleFormState {
  name: string;
  manufacturerId: string;
  additionalManufacturerIds: string[];
  alsoKnownAs: string[];
  generation: string;
  displayType: string;
  lcdType: string;
  battery: string;
  batteryLifeMonths: string;
  chargeTimeHours: string;
  lightType: string;
  displayColor: string;
  textColor: string;
  sensors: string[];
  displayLayout: string;
  viewingAngle: string;
  dstSupport: string;
  calendarType: string;
  stopwatchPrecision: string;
  powerSource: string;
  syncSource: string;
  timeSystems: string[];
  alarmTypes: string[];
  description: string;
  aiDescription: string;
  source: string;
  sourceUrl: string;
}

function emptyForm(
  defaultManufacturerId?: string,
  defaultName?: string,
): ModuleFormState {
  return {
    name: defaultName || '',
    manufacturerId: defaultManufacturerId || '',
    additionalManufacturerIds: [],
    alsoKnownAs: [],
    generation: '',
    displayType: '',
    lcdType: '',
    battery: '',
    batteryLifeMonths: '',
    chargeTimeHours: '',
    lightType: '',
    displayColor: '',
    textColor: '',
    sensors: [],
    displayLayout: '',
    viewingAngle: '',
    dstSupport: '',
    calendarType: '',
    stopwatchPrecision: '',
    powerSource: '',
    syncSource: '',
    timeSystems: [],
    alarmTypes: [],
    description: '',
    aiDescription: '',
    source: '',
    sourceUrl: '',
  };
}

function moduleToForm(m: ElectronicModule): ModuleFormState {
  return {
    name: m.name || '',
    manufacturerId: m.manufacturerId || '',
    additionalManufacturerIds: m.additionalManufacturerIds || [],
    alsoKnownAs: m.alsoKnownAs || [],
    generation: m.generation || '',
    displayType: m.displayType || '',
    lcdType: m.lcdType || '',
    battery: m.battery || '',
    batteryLifeMonths:
      typeof m.batteryLifeMonths === 'number' ? String(m.batteryLifeMonths) : '',
    chargeTimeHours:
      typeof m.chargeTimeHours === 'number' ? String(m.chargeTimeHours) : '',
    lightType: m.lightType || '',
    displayColor: m.displayColor || '',
    textColor: m.textColor || '',
    sensors: m.sensors || [],
    displayLayout: m.displayLayout || '',
    viewingAngle: m.viewingAngle || '',
    dstSupport: m.dstSupport || '',
    calendarType: m.calendarType || '',
    stopwatchPrecision: m.stopwatchPrecision || '',
    powerSource: m.powerSource || '',
    syncSource: m.syncSource || '',
    timeSystems: m.timeSystems || [],
    alarmTypes: m.alarmTypes || [],
    description: m.description || '',
    aiDescription: m.aiDescription || '',
    source: m.source || '',
    sourceUrl: m.sourceUrl || '',
  };
}

/** A single field in the AI apply-review diff. */
interface AiFieldDiff {
  key: string;
  label: string;
  current: string;
  proposed: string;
  changed: boolean;
  apply: () => void;
}

interface ElectronicModuleEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Existing module to edit; null = create new. */
  module?: ElectronicModule | null;
  /** Pre-fill manufacturer when launched from a manufacturer detail page. */
  defaultManufacturerId?: string;
  /** Pre-fill the name on create (e.g. seeded from a ref's legacy module value). */
  defaultName?: string;
  /**
   * Called after a successful save so the parent can refresh / link. NOTE: the
   * editor no longer self-closes on save — it stays open (transitioning a
   * just-created module into an edit session) so AI enrichment can run against
   * the freshly-saved `moduleId`. Callers should refresh their data here but
   * must NOT force the modal closed; closing is the user's explicit action.
   */
  onSaved?: (savedId: string) => void;
  onDeleted?: (deletedId: string) => void;
  /**
   * When set, auto-fire `enrichElectronicModule` immediately after the FIRST
   * (create) save and open the review panel with the results — used by the AI
   * Module Lookup "Create this module" chain so the user skips the manual
   * "click AI Enrichment after save" step. Failures surface in the panel and
   * leave the editor usable for a manual retry.
   */
  autoEnrichOnSave?: boolean;
}

export function ElectronicModuleEditorModal({
  isOpen,
  onClose,
  module,
  defaultManufacturerId,
  defaultName,
  onSaved,
  onDeleted,
  autoEnrichOnSave = false,
}: ElectronicModuleEditorModalProps) {
  // `savedModuleId` is the source of truth for "has this module been persisted
  // yet" — seeded from an existing module, then set on the first create-save so
  // the AI enrichment button (which needs a real `moduleId`) enables in place
  // without waiting for a reopen. `module` (the prop) may stay null across a
  // create, so gating on it would keep the button stuck disabled.
  const [savedModuleId, setSavedModuleId] = useState<string | null>(
    module?.id ?? null,
  );
  const canDelete = !!module;
  const isSavedView = !!module || savedModuleId != null;
  const [form, setForm] = useState<ModuleFormState>(() =>
    module ? moduleToForm(module) : emptyForm(defaultManufacturerId, defaultName),
  );
  const [images, setImages] = useState<CalibreImage[]>(module?.images || []);
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Image add controls
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageCaption, setNewImageCaption] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Manufacturer quick-create
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [quickCreateName, setQuickCreateName] = useState('');
  const [quickCreateTarget, setQuickCreateTarget] = useState<
    'primary' | 'co-developer'
  >('primary');

  // AI enrich
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiState, setAiState] = useState<ModuleAiEnrichState>({});
  const [refInfo, setRefInfo] = useState('');
  const [aiApplyOpen, setAiApplyOpen] = useState(false);
  const [aiFieldDiffs, setAiFieldDiffs] = useState<AiFieldDiff[]>([]);
  const [aiFieldSel, setAiFieldSel] = useState<Record<string, boolean>>({});

  // Reset form whenever the modal opens for a different subject.
  useEffect(() => {
    if (!isOpen) return;
    setForm(
      module ? moduleToForm(module) : emptyForm(defaultManufacturerId, defaultName),
    );
    setImages(module?.images || []);
    setSavedModuleId(module?.id ?? null);
    setShowAiPanel(false);
    setAiState({});
    setRefInfo('');
    setAiApplyOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, module?.id]);

  // The manufacturer catalogue, once per open. From
  // `watchkeeper_manufacturers`: the two dropdowns below render a label and
  // write back an id, which is the whole of what this modal needs.
  //
  // Through the shared hook, so a manufacturer created in the quick-create modal
  // below — or on any other screen — appears here without a page reload.
  const { manufacturers, loading: manufacturersLoading } =
    useManufacturerCatalogue({ enabled: isOpen });

  // Resolve storage paths → display URLs for the image grid.
  useEffect(() => {
    let cancelled = false;
    const toResolve = images
      .map((img) => img.storagePath)
      .filter((p): p is string => !!p && !(p in resolvedUrls));
    if (toResolve.length === 0) return;
    (async () => {
      const entries = await Promise.all(
        toResolve.map(async (path) => {
          try {
            return [path, await getImageUrl(path)] as const;
          } catch {
            return [path, ''] as const;
          }
        }),
      );
      if (cancelled) return;
      setResolvedUrls((prev) => {
        const next = { ...prev };
        for (const [path, url] of entries) if (url) next[path] = url;
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [images, resolvedUrls]);

  const updateForm = (patch: Partial<ModuleFormState>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  // ---- Manufacturer selection -------------------------------------------------
  const handleManufacturerChange = (value: string) => {
    if (value === '__create__') {
      setQuickCreateTarget('primary');
      setQuickCreateName(form.name);
      setQuickCreateOpen(true);
      return;
    }
    updateForm({ manufacturerId: value });
  };

  const handleAddCoDeveloper = (value: string) => {
    if (!value) return;
    if (value === '__create__') {
      setQuickCreateTarget('co-developer');
      setQuickCreateName('');
      setQuickCreateOpen(true);
      return;
    }
    setForm((prev) =>
      value === prev.manufacturerId ||
      prev.additionalManufacturerIds.includes(value)
        ? prev
        : {
            ...prev,
            additionalManufacturerIds: [
              ...prev.additionalManufacturerIds,
              value,
            ],
          },
    );
  };

  const handleRemoveCoDeveloper = (value: string) =>
    setForm((prev) => ({
      ...prev,
      additionalManufacturerIds: prev.additionalManufacturerIds.filter(
        (id) => id !== value,
      ),
    }));

  // No local splice: `createMovementManufacturer` records the new manufacturer
  // in the catalogue overlay and broadcasts the change, so the dropdowns above
  // already carry it whether or not Algolia has indexed it yet. See
  // `manufacturerCatalogueOverlay.ts`. All that is left here is selecting it.
  const handleManufacturerCreated = (id: string) => {
    if (quickCreateTarget === 'primary') updateForm({ manufacturerId: id });
    else handleAddCoDeveloper(id);
    setQuickCreateOpen(false);
  };

  // ---- Images -----------------------------------------------------------------
  const activeDocId = useMemo(
    () =>
      savedModuleId ||
      (form.name.trim()
        ? buildElectronicModuleDocId(form.name, form.manufacturerId || null)
        : ''),
    [savedModuleId, form.name, form.manufacturerId],
  );

  const uploadAndAppend = async (file: File | Blob, fileName: string) => {
    if (!activeDocId) {
      toast.error('Enter a module name before adding images');
      return;
    }
    if (images.length >= MAX_IMAGES) {
      toast.error(`Max ${MAX_IMAGES} images`);
      return;
    }
    setUploadingImage(true);
    try {
      const storagePath = await uploadElectronicModuleImage(
        activeDocId,
        file,
        fileName,
      );
      const caption = newImageCaption.trim();
      const source = form.source.trim() || 'manual';
      setImages((prev) => [
        ...prev,
        { storagePath, source, ...(caption ? { caption } : {}) },
      ]);
      setNewImageUrl('');
      setNewImageCaption('');
    } catch (err) {
      console.error('Failed to upload module image:', err);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddImageFromUrl = async () => {
    const url = newImageUrl.trim();
    if (!url) {
      toast.error('Image URL is required');
      return;
    }
    setUploadingImage(true);
    try {
      const blob = await fetchImageBlob(url);
      const fileName = url.split('/').pop()?.split('?')[0] || 'image.jpg';
      await uploadAndAppend(blob, fileName);
    } catch (err) {
      console.error('Failed to fetch image from URL:', err);
      toast.error('Failed to fetch image from URL');
      setUploadingImage(false);
    }
  };

  const handleAddImageFromFile = async (file: File) => {
    await uploadAndAppend(file, file.name);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpdateImageSource = (idx: number, source: string) =>
    setImages((prev) =>
      prev.map((img, i) => (i === idx ? { ...img, source } : img)),
    );

  const handleRemoveImage = async (idx: number) => {
    const img = images[idx];
    setImages((prev) => prev.filter((_, i) => i !== idx));
    if (img?.storagePath) {
      try {
        await deleteCalibreImage(img.storagePath);
      } catch (err) {
        console.warn('Failed to delete module image:', err);
      }
    }
  };

  // ---- AI enrich --------------------------------------------------------------
  // The `enrichElectronicModule` callable reads the persisted module doc, so it
  // requires a saved module — gated on `savedModuleId`. `idOverride` lets the
  // auto-enrich chain pass the just-saved id before React commits the state
  // update from the same tick.
  const runAiEnrich = async (idOverride?: string) => {
    const moduleId = idOverride ?? savedModuleId;
    if (!moduleId) return;
    setAiState((s) => ({ ...s, loading: true, error: undefined }));
    try {
      const result = await enrichElectronicModuleWithAI(
        moduleId,
        refInfo.trim() || undefined,
      );
      setAiState({ result });
    } catch (err) {
      console.error('Module AI enrichment failed:', err);
      setAiState({
        error: err instanceof Error ? err.message : 'AI enrichment failed',
      });
    }
  };

  const openAiApplyReview = () => {
    const result = aiState.result;
    if (!result) return;
    const proposed = moduleResultToFields(result);
    const diffs: AiFieldDiff[] = [];

    const pushStr = (
      key: keyof ModuleFormState & string,
      label: string,
      current: string,
      next: string | undefined,
    ) => {
      if (next == null || next === '') return;
      diffs.push({
        key,
        label,
        current,
        proposed: next,
        changed: current !== next,
        apply: () => updateForm({ [key]: next } as Partial<ModuleFormState>),
      });
    };

    const pushArr = (
      key: 'sensors' | 'timeSystems' | 'alarmTypes' | 'alsoKnownAs',
      label: string,
      current: string[],
      next: string[] | undefined,
      merge = false,
    ) => {
      if (!next || next.length === 0) return;
      const value = merge ? [...new Set([...current, ...next])] : next;
      diffs.push({
        key,
        label,
        current: current.join(', '),
        proposed: value.join(', '),
        changed: current.join(', ') !== value.join(', '),
        apply: () => updateForm({ [key]: value } as Partial<ModuleFormState>),
      });
    };

    pushStr('name', 'Name', form.name, proposed.name);
    pushStr('aiDescription', 'AI description', form.aiDescription, proposed.aiDescription);
    pushStr('displayType', 'Display type', form.displayType, proposed.displayType);
    pushStr('lcdType', 'LCD type', form.lcdType, proposed.lcdType);
    pushStr('lightType', 'Light type', form.lightType, proposed.lightType);
    pushStr('displayColor', 'Display colour', form.displayColor, proposed.displayColor);
    pushStr('textColor', 'Text colour', form.textColor, proposed.textColor);
    pushStr('battery', 'Battery', form.battery, proposed.battery);
    pushStr('batteryLifeMonths', 'Battery life (months)', form.batteryLifeMonths, proposed.batteryLifeMonths);
    pushStr('chargeTimeHours', 'Charge time (hours)', form.chargeTimeHours, proposed.chargeTimeHours);
    pushStr('generation', 'Generation', form.generation, proposed.generation);
    pushStr('displayLayout', 'Display layout', form.displayLayout, proposed.displayLayout);
    pushStr('viewingAngle', 'Viewing angle', form.viewingAngle, proposed.viewingAngle);
    pushStr('dstSupport', 'DST support', form.dstSupport, proposed.dstSupport);
    pushStr('calendarType', 'Calendar type', form.calendarType, proposed.calendarType);
    pushStr('stopwatchPrecision', 'Stopwatch precision', form.stopwatchPrecision, proposed.stopwatchPrecision);
    pushStr('powerSource', 'Power source', form.powerSource, proposed.powerSource);
    pushStr('syncSource', 'Sync source', form.syncSource, proposed.syncSource);
    pushArr('sensors', 'Sensors', form.sensors, proposed.sensors);
    pushArr('timeSystems', 'Time systems', form.timeSystems, proposed.timeSystems);
    pushArr('alarmTypes', 'Alarm types', form.alarmTypes, proposed.alarmTypes);
    pushArr('alsoKnownAs', 'Also known as', form.alsoKnownAs, proposed.alsoKnownAs, true);
    pushStr('source', 'Source', form.source, proposed.source);
    pushStr('sourceUrl', 'Source URL', form.sourceUrl, proposed.sourceUrl);

    setAiFieldDiffs(diffs);
    setAiFieldSel(Object.fromEntries(diffs.map((d) => [d.key, d.changed])));
    setAiApplyOpen(true);
  };

  const applyAiFields = (keys: Set<string>) => {
    for (const d of aiFieldDiffs) if (keys.has(d.key)) d.apply();
    setAiApplyOpen(false);
    setShowAiPanel(false);
    setAiState({});
    setAiFieldDiffs([]);
    setAiFieldSel({});
    toast.success(`Applied ${keys.size} AI field${keys.size === 1 ? '' : 's'}`);
  };

  const aiSelectedCount = aiFieldDiffs.filter((d) => aiFieldSel[d.key]).length;

  // ---- Save / delete ----------------------------------------------------------
  const parseNum = (v: string): number | undefined => {
    const t = v.trim();
    if (!t) return undefined;
    const n = Number(t);
    return Number.isFinite(n) ? n : undefined;
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Module name is required');
      return;
    }
    // First save = create (no prior persisted id). Used to label the toast and
    // to gate the auto-enrich chain to the initial create only.
    const priorId = savedModuleId ?? module?.id ?? null;
    const isFirstSave = !priorId;
    setSaving(true);
    try {
      const savedId = await saveElectronicModule({
        id: priorId ?? undefined,
        name: form.name,
        manufacturerId: form.manufacturerId || undefined,
        additionalManufacturerIds: form.additionalManufacturerIds,
        alsoKnownAs: form.alsoKnownAs,
        generation: form.generation || undefined,
        displayType: form.displayType || undefined,
        lcdType: form.lcdType || undefined,
        battery: form.battery || undefined,
        batteryLifeMonths: parseNum(form.batteryLifeMonths),
        chargeTimeHours: parseNum(form.chargeTimeHours),
        lightType: form.lightType || undefined,
        displayColor: form.displayColor || undefined,
        textColor: form.textColor || undefined,
        sensors: form.sensors,
        displayLayout: form.displayLayout || undefined,
        viewingAngle: form.viewingAngle || undefined,
        dstSupport: form.dstSupport || undefined,
        calendarType: form.calendarType || undefined,
        stopwatchPrecision: form.stopwatchPrecision || undefined,
        powerSource: form.powerSource || undefined,
        syncSource: form.syncSource || undefined,
        timeSystems: form.timeSystems,
        alarmTypes: form.alarmTypes,
        description: form.description || undefined,
        aiDescription: form.aiDescription || undefined,
        source: form.source || undefined,
        sourceUrl: form.sourceUrl || undefined,
        images,
      });
      toast.success(`Module ${isFirstSave ? 'created' : 'updated'}`);
      // Persist the id locally so the AI enrichment button enables in place, and
      // keep the modal open (no onClose) so the user can enrich the module they
      // just created without reopening it. Closing is now an explicit action.
      setSavedModuleId(savedId);
      onSaved?.(savedId);
      // Auto-chain enrichment after the first (create) save when requested (the
      // AI Module Lookup "Create this module" flow): reveal + run the review
      // panel with the just-saved id. Errors surface in the panel; the editor
      // stays usable for a manual retry.
      if (autoEnrichOnSave && isFirstSave) {
        setShowAiPanel(true);
        void runAiEnrich(savedId);
      }
    } catch (err) {
      console.error('Failed to save module:', err);
      toast.error(
        err instanceof Error ? err.message : 'Failed to save module',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!module) return;
    if (
      !window.confirm(
        `Delete module "${module.name}"? This cannot be undone. References linked to it keep their cached electronics until re-linked.`,
      )
    )
      return;
    setSaving(true);
    try {
      await deleteElectronicModule(module.id);
      toast.success('Module deleted');
      onDeleted?.(module.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete module:', err);
      toast.error('Failed to delete module');
    } finally {
      setSaving(false);
    }
  };

  const coDeveloperOptions = manufacturers.filter(
    (m) =>
      m.id !== form.manufacturerId &&
      !form.additionalManufacturerIds.includes(m.id),
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          isSavedView
            ? `Edit module — ${module?.name || form.name}`
            : 'New electronic module'
        }
        size="xl"
      >
        <div className="space-y-5">
          {/* Identity */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Identity
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => updateForm({ name: e.target.value })}
                  placeholder="e.g. 3159, UTD-8000"
                  className={inputClass}
                />
              </div>
              <LookupFieldSelect
                label="Generation"
                value={form.generation}
                lookupCollection="lookup_module_generation"
                onChange={(v) => updateForm({ generation: v })}
                placeholder="e.g. Gen 2"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manufacturer
                </label>
                <select
                  value={form.manufacturerId}
                  onChange={(e) => handleManufacturerChange(e.target.value)}
                  disabled={manufacturersLoading}
                  className={inputClass}
                >
                  <option value="">
                    {manufacturersLoading ? 'Loading…' : '— None —'}
                  </option>
                  <option value="__create__">+ Create new manufacturer…</option>
                  {manufacturers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName || m.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Co-developers
                </label>
                {form.additionalManufacturerIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {form.additionalManufacturerIds.map((id) => {
                      const m = manufacturers.find((x) => x.id === id);
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-brand-50 text-brand-800 border border-brand-200"
                        >
                          {m ? m.displayName || m.name : id}
                          <button
                            type="button"
                            onClick={() => handleRemoveCoDeveloper(id)}
                            className="text-brand-500 hover:text-brand-700"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <select
                  value=""
                  onChange={(e) => {
                    handleAddCoDeveloper(e.target.value);
                    e.target.value = '';
                  }}
                  disabled={manufacturersLoading}
                  className={inputClass}
                >
                  <option value="">+ Add a co-developer…</option>
                  <option value="__create__">+ Create new manufacturer…</option>
                  {coDeveloperOptions.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName || m.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <TagInput
              label="Also known as"
              values={form.alsoKnownAs}
              onChange={(v) => updateForm({ alsoKnownAs: v })}
              placeholder="Add alternative name / number…"
            />
          </section>

          {/* Display & electronics */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Display &amp; electronics
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <LookupFieldSelect
                label="Display type"
                value={form.displayType}
                lookupCollection="lookup_display_type"
                onChange={(v) => updateForm({ displayType: v })}
              />
              <LookupFieldSelect
                label="LCD type"
                value={form.lcdType}
                lookupCollection="lookup_lcd_type"
                onChange={(v) => updateForm({ lcdType: v })}
              />
              <LookupFieldSelect
                label="Light type"
                value={form.lightType}
                lookupCollection="lookup_light_type"
                onChange={(v) => updateForm({ lightType: v })}
              />
              <LookupFieldSelect
                label="Display colour"
                value={form.displayColor}
                lookupCollection="lookup_display_colors"
                onChange={(v) => updateForm({ displayColor: v })}
              />
              <LookupFieldSelect
                label="Text colour"
                value={form.textColor}
                lookupCollection="lookup_text_colors"
                onChange={(v) => updateForm({ textColor: v })}
              />
              <LookupFieldSelect
                label="Display layout"
                value={form.displayLayout}
                lookupCollection="lookup_display_layout"
                onChange={(v) => updateForm({ displayLayout: v })}
              />
              <LookupFieldSelect
                label="Viewing angle"
                value={form.viewingAngle}
                lookupCollection="lookup_viewing_angle"
                onChange={(v) => updateForm({ viewingAngle: v })}
              />
              <LookupFieldSelect
                label="Battery"
                value={form.battery}
                lookupCollection="lookup_battery_type"
                onChange={(v) => updateForm({ battery: v })}
              />
              <LookupFieldSelect
                label="Power source"
                value={form.powerSource}
                lookupCollection="lookup_power_source"
                onChange={(v) => updateForm({ powerSource: v })}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Battery life (months)
                </label>
                <input
                  type="number"
                  value={form.batteryLifeMonths}
                  onChange={(e) =>
                    updateForm({ batteryLifeMonths: e.target.value })
                  }
                  className={inputClass}
                  placeholder="e.g. 24"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Charge time (hours)
                </label>
                <input
                  type="number"
                  value={form.chargeTimeHours}
                  onChange={(e) =>
                    updateForm({ chargeTimeHours: e.target.value })
                  }
                  className={inputClass}
                  placeholder="e.g. 5"
                />
              </div>
            </div>
            <LookupMultiSelect
              label="Sensors"
              values={form.sensors}
              lookupCollection="lookup_sensors"
              onChange={(v) => updateForm({ sensors: v })}
            />
          </section>

          {/* Time & functions */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Time &amp; functions
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <LookupFieldSelect
                label="Calendar type"
                value={form.calendarType}
                lookupCollection="lookup_calendar_type"
                onChange={(v) => updateForm({ calendarType: v })}
              />
              <LookupFieldSelect
                label="DST support"
                value={form.dstSupport}
                lookupCollection="lookup_dst_support"
                onChange={(v) => updateForm({ dstSupport: v })}
              />
              <LookupFieldSelect
                label="Stopwatch precision"
                value={form.stopwatchPrecision}
                lookupCollection="lookup_stopwatch_precision"
                onChange={(v) => updateForm({ stopwatchPrecision: v })}
              />
              <LookupFieldSelect
                label="Sync source"
                value={form.syncSource}
                lookupCollection="lookup_sync_source"
                onChange={(v) => updateForm({ syncSource: v })}
              />
            </div>
            <LookupMultiSelect
              label="Time systems"
              values={form.timeSystems}
              lookupCollection="lookup_time_systems"
              onChange={(v) => updateForm({ timeSystems: v })}
            />
            <LookupMultiSelect
              label="Alarm types"
              values={form.alarmTypes}
              lookupCollection="lookup_alarm_types"
              onChange={(v) => updateForm({ alarmTypes: v })}
            />
          </section>

          {/* Descriptions */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Descriptions
              </h4>
              <button
                type="button"
                onClick={() => setShowAiPanel((v) => !v)}
                disabled={!savedModuleId}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={
                  savedModuleId
                    ? 'Identify and fill in specs using AI'
                    : 'Save the module first — AI enrichment reads the saved module'
                }
              >
                {showAiPanel ? '✕ Hide AI' : '✨ Suggest with AI'}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description{' '}
                  <span className="font-normal text-gray-400">
                    (manually authored)
                  </span>
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => updateForm({ description: e.target.value })}
                  rows={5}
                  className={inputClass}
                  placeholder="Authoritative, human-written module description…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">
                  AI description{' '}
                  <span className="font-normal text-purple-400">
                    (AI-generated)
                  </span>
                </label>
                <textarea
                  value={form.aiDescription}
                  onChange={(e) =>
                    updateForm({ aiDescription: e.target.value })
                  }
                  rows={5}
                  className="w-full border border-purple-200 bg-purple-50/40 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-400 focus:border-purple-400"
                  placeholder="Populated by Suggest with AI — verify before relying on it."
                />
              </div>
            </div>
            {showAiPanel && (
              <div className="border border-purple-200 bg-purple-50 rounded-lg p-3">
                <div className="text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded mb-3">
                  ⚠️ AI suggestions may contain errors — verify before applying.
                </div>
                <AiElectronicModuleEnrichPanel
                  state={aiState}
                  context={refInfo}
                  onContextChange={setRefInfo}
                  runDisabled={!savedModuleId}
                  disabledReason="Save the module first"
                  onRun={() => runAiEnrich()}
                  onAccept={openAiApplyReview}
                  onClose={() => {
                    setShowAiPanel(false);
                    setAiState({});
                  }}
                />
              </div>
            )}
          </section>

          {/* Source */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Source
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source
                </label>
                <input
                  type="text"
                  value={form.source}
                  onChange={(e) => updateForm({ source: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. casio-module-list, manual"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source URL
                </label>
                <input
                  type="url"
                  value={form.sourceUrl}
                  onChange={(e) => updateForm({ sourceUrl: e.target.value })}
                  className={inputClass}
                  placeholder="https://…"
                />
              </div>
            </div>
          </section>

          {/* Images */}
          <section className="space-y-2">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Images ({images.length}/{MAX_IMAGES})
            </h4>
            <div className="border rounded-lg p-3 bg-gray-50">
              {images.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-3">
                  {images.map((img, idx) => {
                    const displaySrc = img.storagePath
                      ? resolvedUrls[img.storagePath] || ''
                      : img.url || '';
                    return (
                      <div
                        key={idx}
                        className="relative group border rounded-lg overflow-hidden bg-white"
                        style={{ width: 120 }}
                      >
                        {displaySrc ? (
                          <img
                            src={displaySrc}
                            alt={img.caption || `Image ${idx + 1}`}
                            className="w-full h-24 object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.opacity =
                                '0.3';
                            }}
                          />
                        ) : (
                          <div className="w-full h-24 bg-gray-100 flex items-center justify-center text-[10px] text-gray-400">
                            Loading…
                          </div>
                        )}
                        <div className="p-1.5 text-[10px] text-gray-600">
                          <label className="block text-[9px] font-medium text-gray-400 uppercase mb-0.5">
                            Source
                          </label>
                          <input
                            type="text"
                            value={img.source || ''}
                            onChange={(e) =>
                              handleUpdateImageSource(idx, e.target.value)
                            }
                            className="w-full border border-gray-200 rounded px-1 py-0.5 text-[10px] bg-white mb-1"
                          />
                          {img.caption && (
                            <div
                              className="truncate text-gray-500"
                              title={img.caption}
                            >
                              {img.caption}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(idx)}
                          className="absolute top-1 right-1 bg-white border rounded-full text-red-500 hover:text-red-700 hover:bg-red-50 w-6 h-6 flex items-center justify-center shadow-sm"
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {images.length < MAX_IMAGES && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      placeholder="Image URL…"
                      className={`${inputClass} flex-1`}
                    />
                    <input
                      type="text"
                      value={newImageCaption}
                      onChange={(e) => setNewImageCaption(e.target.value)}
                      placeholder="Caption (optional)"
                      className={`${inputClass} flex-1`}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleAddImageFromUrl}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? 'Uploading…' : 'Add from URL'}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void handleAddImageFromFile(file);
                      }}
                      disabled={uploadingImage}
                      className="text-xs text-gray-600"
                    />
                    <span className="text-xs text-gray-400">
                      or upload from your computer
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            {canDelete && (
              <Button
                variant="ghost"
                onClick={handleDelete}
                disabled={saving}
                className="mr-auto text-red-600 hover:text-red-700"
              >
                Delete
              </Button>
            )}
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              {isSavedView ? 'Close' : 'Cancel'}
            </Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              {isSavedView ? 'Save changes' : 'Create module'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* AI apply-review diff */}
      <Modal
        isOpen={aiApplyOpen}
        onClose={() => setAiApplyOpen(false)}
        title="Apply AI suggestions"
        size="lg"
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>
              Tick the fields to apply, then confirm. Applying overwrites the
              current value. Per-field confidence is shown in the AI panel above.
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <button
              type="button"
              onClick={() =>
                setAiFieldSel(
                  Object.fromEntries(aiFieldDiffs.map((d) => [d.key, true])),
                )
              }
              className="text-brand-600 hover:text-brand-800 underline"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() =>
                setAiFieldSel(
                  Object.fromEntries(aiFieldDiffs.map((d) => [d.key, false])),
                )
              }
              className="text-gray-500 hover:text-gray-700 underline"
            >
              Select none
            </button>
            <button
              type="button"
              onClick={() =>
                setAiFieldSel(
                  Object.fromEntries(
                    aiFieldDiffs.map((d) => [d.key, d.changed]),
                  ),
                )
              }
              className="text-gray-500 hover:text-gray-700 underline"
            >
              Changed only
            </button>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[26rem] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 text-gray-500">
                <tr>
                  <th className="w-10 px-3 py-2" />
                  <th className="text-left font-medium px-3 py-2">Field</th>
                  <th className="text-left font-medium px-3 py-2">Current</th>
                  <th className="text-left font-medium px-3 py-2">AI proposed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {aiFieldDiffs.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-3 py-4 text-center text-gray-400"
                    >
                      No suggestions returned.
                    </td>
                  </tr>
                ) : (
                  aiFieldDiffs.map((d) => (
                    <tr key={d.key} className={d.changed ? undefined : 'opacity-60'}>
                      <td className="px-3 py-2 align-top">
                        <input
                          type="checkbox"
                          checked={!!aiFieldSel[d.key]}
                          onChange={(e) =>
                            setAiFieldSel((prev) => ({
                              ...prev,
                              [d.key]: e.target.checked,
                            }))
                          }
                          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 mt-0.5"
                        />
                      </td>
                      <td className="px-3 py-2 align-top text-gray-800">
                        {d.label}
                        {!d.changed && (
                          <span className="ml-1 text-[10px] text-gray-400">
                            (same)
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top font-mono text-gray-500 break-words max-w-[14rem]">
                        {d.current || '—'}
                      </td>
                      <td
                        className={`px-3 py-2 align-top font-mono break-words max-w-[14rem] ${
                          d.changed ? 'text-emerald-700' : 'text-gray-500'
                        }`}
                      >
                        {d.proposed}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAiApplyOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                applyAiFields(new Set(aiFieldDiffs.map((d) => d.key)))
              }
            >
              Apply all
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={aiSelectedCount === 0}
              onClick={() =>
                applyAiFields(
                  new Set(
                    aiFieldDiffs
                      .filter((d) => aiFieldSel[d.key])
                      .map((d) => d.key),
                  ),
                )
              }
            >
              Apply selected ({aiSelectedCount})
            </Button>
          </div>
        </div>
      </Modal>

      <MovementManufacturerQuickCreate
        isOpen={quickCreateOpen}
        initialName={quickCreateName}
        onCancel={() => setQuickCreateOpen(false)}
        onCreated={handleManufacturerCreated}
      />
    </>
  );
}
